import React, { useEffect, useState, useCallback } from 'react';
import { Container, Card, Table, Button, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faCheck } from '@fortawesome/free-solid-svg-icons';
import api from '../api';

// Errori restituiti da Mirakl per le offerte inviate dal sistema (es. "The product does not exist").
// Un nuovo invio riuscito per lo stesso SKU li risolve da solo; "Segna come risolto" li toglie a mano.
export default function ErroriOfferteIBS() {
  const [righe, setRighe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aggiornando, setAggiornando] = useState(false);
  const [error, setError] = useState(null);

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/mirakl/errori-offerte/');
      setRighe(res.data || []);
    } catch (e) {
      setError('Impossibile caricare gli errori delle offerte.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const controllaOra = async () => {
    setAggiornando(true);
    try {
      const res = await api.post('/mirakl/errori-offerte/controlla');
      toast.success(`Controllate ${res.data.import_controllati} importazioni, ${res.data.nuovi_errori} nuovi errori`);
      await carica();
    } catch (e) {
      toast.error('Controllo su Mirakl non riuscito');
    } finally {
      setAggiornando(false);
    }
  };

  const segnaRisolto = async (id) => {
    try {
      await api.post(`/mirakl/errori-offerte/${id}/risolto`);
      setRighe(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      toast.error('Operazione non riuscita');
    }
  };

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Errori offerte IBS</h2>
        <Button variant="outline-secondary" onClick={controllaOra} disabled={aggiornando}>
          {aggiornando ? <Spinner as="span" animation="border" size="sm" /> : <FontAwesomeIcon icon={faSync} className="me-2" />}
          Controlla ora
        </Button>
      </div>
      <p className="text-muted small">
        Qui compaiono gli errori che IBS/Mirakl restituisce dopo l'invio di una offerta (di norma entro 10-15 minuti).
        Se correggi la scheda e riinvii l'offerta con successo, l'avviso si chiude da solo.
      </p>
      {error && <Alert variant="danger">{error}</Alert>}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-4 text-center"><Spinner animation="border" /></div>
          ) : righe.length === 0 ? (
            <div className="p-4 text-center text-muted">Nessun errore: tutte le offerte inviate sono state accettate da IBS.</div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Prodotto</th>
                  <th>SKU</th>
                  <th>Problema</th>
                  <th>Data</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {righe.map(r => (
                  <tr key={r.id}>
                    <td>{r.link ? <Link to={r.link}>{r.titolo_prodotto || r.sku}</Link> : (r.titolo_prodotto || 'Prodotto non trovato')}</td>
                    <td className="font-monospace small">{r.sku}</td>
                    <td>
                      {r.messaggio}
                      <div className="text-muted small">{r.messaggio_originale}</div>
                    </td>
                    <td className="small text-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString('it-IT') : ''}</td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-success" onClick={() => segnaRisolto(r.id)} title="Segna come risolto">
                        <FontAwesomeIcon icon={faCheck} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
